import React, { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import axios from 'axios';
import env from '../../config/env';

interface Report {
  id: number;
  ad_title: string;
  reason: string;
  created_at: string;
  status: string;
}

const ReportsPage: React.FC = () => {
  const location = useLocation();
  const params = new URLSearchParams(location.search);
  const userId = params.get('user_id');

  const [reports, setReports] = useState<Report[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!userId) {
      setLoading(false);
      return;
    }
    axios
      .get(`${env.apiUrl}/getUserReports?user_id=${userId}`)
      .then((res) => {
        if (res.data.status) {
          setReports(res.data.reports);
        }
      })
      .finally(() => setLoading(false));
  }, [userId]);

  return (
    <div style={styles.container}>
      {/* <div style={styles.header}>
        <span style={styles.dash}>—</span>
        <h2 style={styles.title}>Reports</h2>
      </div> */}

      <h1 style={styles.pageTitle}>Your Submitted Reports</h1>

      <p style={styles.introText}>
        Below are the reports you have submitted. Our team reviews each case carefully according to platform policies.
      </p>

      <div style={styles.noteBox}>
        <p style={styles.noteTitle}>Important Note</p>
        <p style={styles.noteText}>
          All reports are reviewed confidentially. Submitting a report does not guarantee immediate action, as each case
          is evaluated based on evidence and policy standards.
        </p>
      </div>

      <p style={styles.sectionLabel}>Report Details</p>
      <div style={styles.divider} />

      {loading ? (
        <p style={styles.emptyText}>Loading...</p>
      ) : reports.length === 0 ? (
        <p style={styles.emptyText}>No reports submitted.</p>
      ) : (
        reports.map((r, i) => (
          <div key={r.id}>
            <div style={styles.reportRow}>
              <p style={styles.reportText}>Reported Account / Ad: {r.ad_title}</p>
              <p style={styles.reportText}>Reason: {r.reason}</p>
              <p style={styles.reportText}>Date Submitted: {r.created_at}</p>
            </div>
            {i < reports.length - 1 && <div style={styles.rowDivider} />}
          </div>
        ))
      )}
    </div>
  );
};

const styles: { [key: string]: React.CSSProperties } = {
  container: {
    fontFamily: "'Segoe UI', Arial, sans-serif",
    backgroundColor: '#fff',
    minHeight: '100vh',
    padding: '24px 20px',
    boxSizing: 'border-box',
    maxWidth: 480,
    margin: '0 auto',
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    marginBottom: 24,
  },
  dash: {
    fontSize: 18,
    fontWeight: 700,
    color: '#111',
    marginRight: 12,
  },
  title: {
    fontSize: 16,
    fontWeight: 700,
    color: '#111',
    margin: 0,
  },
  pageTitle: {
    fontSize: 22,
    fontWeight: 700,
    color: '#111',
    textAlign: 'center',
    marginBottom: 16,
    marginTop: 0,
  },
  introText: {
    fontSize: 13,
    color: '#333',
    textAlign: 'center',
    lineHeight: 1.7,
    marginBottom: 24,
  },
  noteBox: {
    textAlign: 'center',
    marginBottom: 28,
  },
  noteTitle: {
    fontSize: 14,
    fontWeight: 700,
    color: '#111',
    marginBottom: 8,
    marginTop: 0,
  },
  noteText: {
    fontSize: 13,
    color: '#333',
    lineHeight: 1.7,
    margin: 0,
    textAlign: 'center',
  },
  sectionLabel: {
    fontSize: 14,
    fontWeight: 700,
    color: '#111',
    marginBottom: 6,
    marginTop: 0,
  },
  divider: {
    borderBottom: '2px solid #111',
    marginBottom: 14,
  },
  reportRow: {
    paddingTop: 10,
    paddingBottom: 10,
  },
  reportText: {
    fontSize: 13,
    color: '#333',
    margin: '3px 0',
    lineHeight: 1.6,
  },
  rowDivider: {
    borderBottom: '1px solid #ddd',
    marginBottom: 2,
  },
  emptyText: {
    fontSize: 13,
    color: '#666',
    marginTop: 12,
  },
};

export default ReportsPage;
